import { LoadingButton } from "@mui/lab";
import { Box, Button, Checkbox, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from "@mui/material";
import Dialog from '@mui/material/Dialog';
import { makeStyles } from "@mui/styles";
import { VirtualItem } from "@tanstack/react-virtual";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import { WorkspaceContext } from "../../../contexts/WorkspaceContext";
import { WorkspacesContext } from "../../../contexts/WorkspacesContext";
import useBulkDashboards from "../../../hooks/useBulkDashboards";
import useDashboards from '../../../hooks/useDashboards';
import usePaginator from "../../../hooks/usePaginator";
import useWorkspaces from "../../../hooks/useWorkspaces";
import { scrollbarStyle } from "../../../shared/dashboard";
import theme from "../../../theme";
import { Dashboard, Workspace } from "../../../types/files";
import NoDashboardsFoundView from "../../molecules/dashboards-empty/NoDashboardsFoundView";
import DashboardsSearchOrCreate from '../../molecules/dashboards-search-create/DashboardsSearchOrCreate';
import DashboardsTableFilter from "../../molecules/dashboards-table/DashboardsTableFilter";
import DashboardsTableHeader from "../../molecules/dashboards-table/DashboardsTableHeader";
import DashboardsTableRow from "../../molecules/dashboards-table/DashboardsTableRow";
import { TableHeaders } from "../../molecules/workspaces/SharedByAll";
import { collectionKey } from "../../pages/InvestorsPage";
import { FallbackLoading } from "../../templates/loader";
import useBackwardsCompatible from "../../../hooks/useBackwardsCompatible";

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            minWidth: 1430,
            minHeight: 800,
            maxHeight: 800,
            width: 'min-content',
            height: 'fit-content',
            padding: 20,
            borderRadius: 24,
        },
    },
    dialogTitle: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: 'unset',
        color: 'black',
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1.5rem',
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '8px 0',
        height: '100%',
        overflowY: 'auto',
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    selected :{
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1rem',
        color: 'black',
    },
    headers: {
        width: '100%',
        height: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollBehavior: 'smooth',
        ...scrollbarStyle,
    },
    //
    table: {
        width: '100%',
        height: 'calc(100vh - 120px)',
    },
    header: {
        width: '100%',
        height: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollBehavior: 'auto',
        borderBottom: `1px solid ${theme.colors.neutral['400']}`,
        ...scrollbarStyle,
    },
    rows: {
        position: 'relative',
        minWidth: '100%',
        width: 'fit-content',
        height: '100%',
        overflow: 'auto',
        scrollBehavior: 'smooth',
        ...scrollbarStyle,
    },
    row: {
        position: 'absolute',
        top: 0,
        left: 0,
        minWidth: '100%',
        width: 'fit-content',
        height: 'auto',
        borderTop: `1px solid ${theme.colors.neutral['400']}`,
        background: `#fff`,
        "&:hover": {
            background: `rgb(246, 251, 251, 0.8)`,
        },
    },
    cancelButton: {
        width: 100,
        borderRadius: 40,
        background: theme.colors.neutral['100'],
        color: theme.colors.neutral['600'],
        textTransform: 'none',
        fontWeight: 'bold',
        transition: 'ease-in-out 300ms',
    },
    saveButton: {
        width: 100,
        borderRadius: 20,
        background: theme.palette.primary.main,
        color: 'white',
        textTransform: 'none',
        fontWeight: 'bold',
        transition: 'ease-in-out 300ms',
    },
    loadingText: {
        fontFamily: 'Inter',
        fontSize: '1.2rem',
        fontWeight: 700,
        color: theme.colors.neutral['900'],
    },
    loadingTextSub: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 500,
        color: '#9e9e9e',
    },
    loading: {
        width: '100%',
        height: '100%',
        color: theme.palette.primary.main,
    },
    checkBox: {
        padding: 'unset !important',
        marginRight: '4px !important',
        "& > svg": {
            width: '20px !important',
            height: '20px !important',
        }
    },
    checkBoxText: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['700'],
    },
}));

const AddCompaniesToInvestorModal: React.FC<{
    isOpen: boolean,
    workspace?: Workspace,
    onClose: () => void,
}> = ({ isOpen, workspace, onClose }) => {
    const classes = useStyles();
    const { workspaceScores, setWorkspaceScores } = useContext(WorkspacesContext);
    const { dashboards } = useContext(DashboardsContext);
    const { workspace: globalWorkspace } = useContext(WorkspaceContext);
    const { getBulkDashboards, assignToWorkspaces, addToBulk, clearBulk} = useBulkDashboards();
    const { filterDashboardList } = useDashboards();
    const { refreshScores, computeWorkspaceScores } = useWorkspaces();
    const { dashboardWorkspaces } = useBackwardsCompatible();

    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [scoring, setScoring] = useState<boolean>(false);
    const [allChecked, setAllChecked] = useState<boolean>(false);
    const [search, setSearch] = useState<string>('');
    const [dashboardArray, setDashboardArray] = useState<Dashboard[]|null>(null);
    const [sortBy, setSortBy] = useState<string>('Added');
    const [sectors, setSectors] = useState<string[]>([]);
    const [stages, setStages] = useState<string[]>([]);
    const timerRefA = useRef<string | number | NodeJS.Timeout | undefined>(undefined);
    const timerRefB = useRef<string | number | NodeJS.Timeout | undefined>(undefined);
    const modalWrapperRef = useRef<HTMLDivElement>(null);
    const virtualRowsRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);

    const syncScroll = (e?: Event) => {
        e?.preventDefault();

        if (headerRef.current && virtualRowsRef.current)
            headerRef.current.scrollLeft = virtualRowsRef.current.scrollLeft;
    };

    const preventManualScroll = (e: Event) => {
        e.preventDefault();
        if (headerRef.current && virtualRowsRef.current)
            headerRef.current.scrollLeft = virtualRowsRef.current.scrollLeft;
    };

    const attachListeners = () => {
        if (!headerRef.current || !virtualRowsRef.current) return;

        virtualRowsRef.current.addEventListener("scroll", syncScroll);
        headerRef.current.addEventListener("wheel", preventManualScroll, { passive: false });
        headerRef.current.addEventListener("touchmove", preventManualScroll, { passive: false });
    };

    const detachListeners = () => {
        if (!headerRef.current || !virtualRowsRef.current) return;

        virtualRowsRef.current.removeEventListener("scroll", syncScroll);
        headerRef.current.removeEventListener("wheel", preventManualScroll);
        headerRef.current.removeEventListener("touchmove", preventManualScroll);
    };

    const localWorkspace = useMemo(() => workspace ?? globalWorkspace, [globalWorkspace, workspace]);

    const bulkCount = useMemo(() => getBulkDashboards(collectionKey).length, [getBulkDashboards]);

    const dashboardScoreMap: Map<Dashboard, number> = useMemo(() => new Map(
        dashboardArray?.filter(dashboard => dashboardWorkspaces(dashboard)?.includes(localWorkspace!.id))
            ?.map(dashboard => [dashboard,
                workspaceScores.find(({workspaceId}) => workspaceId === localWorkspace?.id)?.scores
                    ?.find(({dashboardId}) => dashboardId === dashboard.id)?.score || 0
            ]) ?? []
    // eslint-disable-next-line
    ), [dashboardArray, localWorkspace, workspaceScores]);

    const sortedDashboards = useMemo(() => {
        if (sortBy === 'Fit score')
            return Array.from(dashboardScoreMap).sort((a, b) => b[1] - a[1]).map(entry => entry[0]);

        return [...dashboardArray ?? []].sort((prev: Dashboard, next: Dashboard) => {
            const prevDate = prev.createdAt;
            const nextDate = next.createdAt;

            return nextDate.localeCompare(prevDate);
        });
    // eslint-disable-next-line
    }, [dashboardArray, sortBy]);

    const filteredDashboards = useMemo(() => {
        let filtered: Dashboard[] = sortedDashboards;

        if (!!sectors.length){
            filtered = filtered.filter(dashboard =>
                sectors.every(key => (dashboard.tags || []).includes(key))
            );
        }

        if (!!stages.length){
            filtered = filtered.filter(dashboard => {
                const stage = dashboard.investmentStage || '';

                return stages.some(key => key === stage);
            });
        }

        return filtered;
    }, [sectors, sortedDashboards, stages]);

    const { allRows, rowVirtualizer, hasNextPage, isFetching } = usePaginator({
        key: 'add-companies-to-investor-modal-166',
        source: filteredDashboards,
        limit: 20,
        itemHeight: 60,
        virtualRowsRef,
    });

    const handleFilter = useCallback(({ tags, stage }) => {
        setSectors(tags);
        setStages(stage);
    }, []);

    const handleRefresh = useCallback(() => {
        if (!!localWorkspace)
            refreshScores([localWorkspace], dashboards, () => setScoring(true));
    }, [localWorkspace, refreshScores, dashboards]);

    const handleAssigntoWorkspace = useCallback(() => {
        setSaving(true);
        assignToWorkspaces(collectionKey, [localWorkspace!.id]).finally(() => {
            setSaving(false);
            onClose();
        });
    }, [assignToWorkspaces, localWorkspace, onClose]);

    useEffect(() => {
        if (!loading) {
            const observer = new MutationObserver(() => {
                if (headerRef.current && virtualRowsRef.current) {
                    clearTimeout(timerRefA.current);
                    timerRefA.current = setTimeout(() => attachListeners(), 500);
                }
            });

            if (modalWrapperRef.current)
                observer.observe(modalWrapperRef.current, { childList: true, subtree: true });
            timerRefA.current = setTimeout(() => attachListeners(), 500);

            return () => {
                observer.disconnect();
                detachListeners();
                clearTimeout(timerRefA.current);
            };
        }
    // eslint-disable-next-line
    }, [isOpen, loading]);

    useEffect(() => {
        if (allChecked)
            (async () => await Promise.all(filteredDashboards.map(dashboard => Promise.resolve(addToBulk(collectionKey, dashboard)))))();
        else
            clearBulk();
    // eslint-disable-next-line
    }, [allChecked]);

    useEffect(() => {
        if (filteredDashboards.length > bulkCount)
            setAllChecked(false);
    // eslint-disable-next-line
    }, [bulkCount]);

    useEffect(() => {
        setSaving(false);
        setScoring(false);
        clearBulk(collectionKey);
    // eslint-disable-next-line
    }, []);

    useEffect(() => {
        timerRefA.current = setTimeout(() => {
            setDashboardArray(filterDashboardList(search || ''));
            clearTimeout(timerRefA.current);
        }, 500);

        return () => {
            clearTimeout(timerRefA.current);
        }
    }, [filterDashboardList, search]);

    useEffect(() => {
        setLoading((!!timerRefA.current && !dashboardArray)|| isFetching);
    }, [dashboardArray, isFetching]);

    useEffect(() => {
        if (!!localWorkspace && scoring) {
           timerRefB.current = setTimeout(() => {
               clearInterval(timerRefB.current);
               computeWorkspaceScores([localWorkspace]).then((updatedWorkspaceScores) => {
                   setWorkspaceScores(updatedWorkspaceScores);
                }).finally(() => setScoring(false));
           }, 3000);
       }

       return () => {
           clearTimeout(timerRefB.current);
       }
    // eslint-disable-next-line
    }, [scoring]);

    return (<>
        <Dialog className={classes.dialog} open={isOpen} onClose={!loading && !scoring ? onClose : undefined} ref={modalWrapperRef}>
            <DialogTitle className={classes.dialogTitle}>
                <Stack direction="row" alignItems="center" justifyContent="flex-start" width="100%">
                    <Box minWidth="400px">
                        <span>{`Add companies to ${localWorkspace?.name}`}</span>
                    </Box>
                    <Box minWidth="400px" maxWidth="800px">
                        <DashboardsSearchOrCreate search mediumFit onSearch={(value) => setSearch(value)} />
                    </Box>
                </Stack>
            </DialogTitle>
            <Divider sx={{ margin: '8px 0'}} />
            <DashboardsTableFilter onFilter={handleFilter} />
            <DialogContent className={classes.dialogContent}>
                <Stack className={classes.table}>
                    {loading ? (<FallbackLoading />) : !!filteredDashboards?.length ? (<>
                        <Stack alignItems="flex-start" justifyContent="flex-start" height="100%">
                            <Box width="100%" height="fit-content" overflow="hidden">
                                <Box className={classes.header} ref={headerRef}>
                                    <DashboardsTableHeader
                                        columns={TableHeaders}
                                        columnSorted={sortBy}
                                        onColumnSort={setSortBy}
                                        onRefreshScores={handleRefresh}
                                        noStatus />
                                </Box>
                            </Box>
                            <Box width="100%" height="100%" overflow="hidden">
                                <Box className={classes.rows} ref={virtualRowsRef}
                                    sx={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
                                    {rowVirtualizer.getVirtualItems().map((virtualRow: VirtualItem) => (
                                        <Box className={classes.row}
                                            sx={{  height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)`, }}
                                            key={'add-companies-to-investor-399-' + virtualRow.index} >
                                            {(virtualRow.index > allRows.length - 1) && hasNextPage
                                                ? (<FallbackLoading />)
                                                : (<DashboardsTableRow
                                                        dashboard={allRows[virtualRow.index]}
                                                        workspace={workspace ?? undefined}
                                                        columns={TableHeaders}
                                                        collectionKey={collectionKey}
                                                        refreshingScore={scoring}
                                                        isPublic noStatus />)}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Stack>
                    </>) : (
                        <Stack className={classes.headers} alignItems="flex-start" justifyContent="flex-start">
                            <DashboardsTableHeader columns={TableHeaders} />
                            <Stack className={classes.rows} alignItems="flex-start" justifyContent="flex-start">
                                <NoDashboardsFoundView
                                    noDataYet={!dashboards.length}
                                    noCompaniesYet={!search && !sectors.length && !stages.length}
                                    noCompaniesFound />
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Stack direction="row" alignItems="center" justifyContent="flex-start" width="100%"
                    sx={{ transform: 'translateX(-10px)'}}>
                    <Checkbox edge="end"
                        className={classes.checkBox}
                        checked={allChecked}
                        onClick={(e: any) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setAllChecked(prev => !prev);
                        }}
                        disableRipple
                    />
                    <Typography className={classes.checkBoxText}>
                        {'Select all'}&nbsp;
                        <Typography component="span" className={classes.checkBoxText}
                            color={`${theme.colors.neutral['600']} !important`} fontWeight="500 !important" display="inline">
                            {`(${filteredDashboards.length} compan${filteredDashboards.length > 1 ? 'ies': 'y'})`}
                        </Typography>
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end" width="100%">
                    {!scoring && (
                        <Typography className={classes.selected}>
                            {`${bulkCount} compan${bulkCount > 1 ? 'ies': 'y'} selected`}
                        </Typography>
                    )}
                    <Button className={classes.cancelButton}
                        disabled={scoring}
                        onClick={onClose}> {'Cancel'} </Button>
                    <LoadingButton variant="contained"
                        className={classes.saveButton}
                        loading={saving}
                        disabled={scoring}
                        onClick={handleAssigntoWorkspace}
                    > {'Add'} </LoadingButton>
                </Stack>
            </DialogActions>
        </Dialog>
    </>);
}

export default AddCompaniesToInvestorModal;
